/* eslint-disable max-lines-per-function */
import classNames from 'clsx';
import { ReactComponent as SharedIcon } from 'img/icons/shared.svg';
import { ReactComponent as AttachIcon } from 'mrg-icons/src/mailru/status/attach.svg';
import React, { memo, ReactElement } from 'react';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { Progress } from 'reactApp/ui/DataListItemRow/components/Progress';
import { Hint } from 'reactApp/ui/Hint/Hint';
import Tip from 'reactApp/ui/Tip/Tip';

import { Checkbox } from './components/Checkbox';
import { Date } from './components/Date';
import { Destination } from './components/Destination';
import { Download } from './components/Download';
import { Expires } from './components/Expires';
import { Favorite } from './components/Favorite';
import { ItemName } from './components/ItemName';
import { Pic } from './components/Pic';
import { Publish } from './components/Publish';
import { Restore } from './components/Restore';
import { Size } from './components/Size';
import styles from './DataListItemRow.css';
import { DataListItemRowProps } from './DataListItemRow.types';

export const DataListItemRow = memo((props: DataListItemRowProps): ReactElement => {
    const { isSelected, isMobile, isSelecting, isActive, denyDoAction, progress, tipData, isFolder } = props;

    const getTip = () =>
        tipData ? (
            <div className={styles.pin}>
                <Tip
                    hideTip={tipData[0].onHideClick}
                    position="top center"
                    text={tipData[0].getTipContent?.(isFolder)}
                    tips={tipData}
                    id={tipData[0].id}
                    show
                    showBubble={false}
                    className={styles.pinTooltip}
                />
            </div>
        ) : null;

    // eslint-disable-next-line complexity
    const renderItem = (props: DataListItemRowProps) => {
        // eslint-disable-next-line sonarjs/max-switch-cases
        switch (props.storage) {
            case EStorageType.archive:
                return (
                    <>
                        {/* Вернуть когда апи поддержит скачивание нескольких <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} isLarge />*/}
                        <Pic isFolder={props.isFolder} kind={props.kind} isVirus={false} ext={props.ext || ''} isLarge={!props.isFolder} />
                        <ItemName name={props.name} />
                        <Size size={props.size} />
                        <Download
                            onClick={props.onDownload}
                            ext={props.ext}
                            size={props.size}
                            id={props.id}
                            storage={EStorageType.archive}
                            isHidden={props.isFolder || props.isEncrypted}
                            isLarge
                        />
                    </>
                );
            case EStorageType.sharedIncoming:
                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} />
                        <Pic isFolder={true} kind={props.kind} author={props.owner} />
                        <ItemName name={props.name} />
                        <div className={styles.access}>{props.access === 'read_only' ? 'Просмотр' : 'Редактирование'}</div>
                        <Size size={props.size} />
                        {!props.isMounted ? (
                            <div className={styles.buttons}>
                                <Hint text="Принять приглашение">
                                    <button
                                        className={classNames({
                                            [styles.button]: true,
                                            [styles.button_accept]: true,
                                        })}
                                        onClick={props.onAccept}
                                    >
                                        Принять
                                    </button>
                                </Hint>
                                <Hint text="Отклонить приглашение">
                                    <button className={styles.button} onClick={props.onReject}>
                                        Отклонить
                                    </button>
                                </Hint>
                            </div>
                        ) : (
                            <Hint text="Настроить доступ">
                                <div className={styles.mount} onClick={props.onMount}>
                                    <SharedIcon width={18} height={12} />
                                </div>
                            </Hint>
                        )}
                    </>
                );
            case EStorageType.home: {
                const isProgress = denyDoAction;

                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} isDisabled={denyDoAction} />
                        <Pic
                            isFolder={props.isFolder}
                            thumbUrl={props.thumbUrl}
                            kind={props.kind}
                            isVirus={!!props.isVirus}
                            ext={props.ext || ''}
                            author={props.author}
                            isAutoDelete={props?.isAutoDelete}
                        />
                        {getTip()}
                        <ItemName name={props.name} ext={props.ext || ''} loading={isProgress} />
                        {!isProgress && (
                            <Favorite
                                isAvailable={!!props.favoritesAvailable}
                                isActive={!!props.isInFavorites}
                                onClick={props.onFavorites}
                            />
                        )}
                        {props.isFolder ? (
                            <Expires expires={props.publicExpires} isAutoDelete={props.isAutoDelete} />
                        ) : (
                            <Date date={props.mtime} isWide />
                        )}
                        <Size size={props.size} />
                        <Download
                            onClick={props.onDownload}
                            ext={props.isFolder ? 'zip' : props.ext}
                            size={props.size}
                            id={props.id}
                            storage={EStorageType.home}
                            show={!isProgress}
                        />
                        {isProgress ? (
                            <Progress className={styles.progress} progress={progress} onClick={props.onCancelUpload} />
                        ) : (
                            <Publish onClick={props.onPublish} isActive={props.isPublic} />
                        )}
                    </>
                );
            }
            case EStorageType.albums: {
                const isProgress = denyDoAction;

                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} isDisabled={denyDoAction} />
                        <Pic
                            isFolder={props.isFolder}
                            thumbUrl={props.thumbUrl}
                            kind={props.kind}
                            isVirus={!!props.isVirus}
                            ext={props.ext || ''}
                            author={props.author}
                            isAutoDelete={props?.isAutoDelete}
                        />
                        <ItemName name={props.name} ext={props.ext || ''} loading={isProgress} />
                        {!isProgress && (
                            <Favorite
                                isAvailable={!!props.favoritesAvailable}
                                isActive={!!props.isInFavorites}
                                onClick={props.onFavorites}
                            />
                        )}
                        <Date date={props.mtime} isWide />
                        <Size size={props.size} />
                        <Download
                            onClick={props.onDownload}
                            ext={props.isFolder ? 'zip' : props.ext}
                            size={props.size}
                            id={props.id}
                            storage={EStorageType.home}
                            show={!isProgress}
                        />
                    </>
                );
            }
            case EStorageType.sharedLinks:
            case EStorageType.sharedAutodelete:
                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} />
                        <Pic
                            isFolder={props.isFolder}
                            thumbUrl={props.thumbUrl}
                            kind={props.kind}
                            isVirus={!!props.isVirus}
                            ext={props.ext || ''}
                            isAutoDelete={props?.isAutoDelete}
                        />
                        <ItemName name={props.name} ext={props.ext || ''} />
                        <Date date={props.mtime} hide />
                        <Expires expires={props.publicExpires} isAutoDelete={props.isAutoDelete} isShared />
                        <Size size={props.size} />
                        <Download
                            onClick={props.onDownload}
                            size={props.size}
                            ext={props.isFolder ? 'zip' : props.ext}
                            id={props.id}
                            storage={EStorageType.sharedLinks}
                        />
                        <Publish onClick={props.onPublish} isActive={props.isPublic} />
                    </>
                );
            case EStorageType.public:
                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} />
                        <Pic
                            isFolder={props.isFolder}
                            thumbUrl={props.thumbUrl}
                            kind={props.kind}
                            isVirus={!!props.isVirus}
                            ext={props.ext || ''}
                            author={props.author}
                        />
                        {getTip()}
                        <ItemName name={props.name} ext={props.ext || ''} />
                        <Date date={props.mtime} />
                        <Size size={props.size} />
                        <Download
                            onClick={props.onDownload}
                            ext={props.isFolder ? 'zip' : props.ext}
                            size={props.size}
                            id={props.id}
                            storage={EStorageType.public}
                        />
                    </>
                );
            case EStorageType.favorites:
                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} />
                        <Pic
                            isFolder={props.isFolder}
                            thumbUrl={props.thumbUrl}
                            isVirus={!!props.isVirus}
                            ext={props.ext || ''}
                            author={props.author}
                            id={props.id}
                            kind={props.kind}
                            isAutoDelete={props?.isAutoDelete}
                        />
                        <ItemName name={props.name} ext={props.ext || ''} />
                        <Favorite isAvailable isActive onClick={props.onFavorites} isActiveHidden={!props.isSelected} />
                        {props.isFolder ? (
                            <Expires expires={props.publicExpires} isAutoDelete={props.isAutoDelete} />
                        ) : (
                            <Date date={props.mtime} isWide />
                        )}
                        <Size size={props.size} />
                        <Download
                            onClick={props.onDownload}
                            ext={props.ext}
                            size={props.size}
                            id={props.id}
                            storage={EStorageType.favorites}
                        />
                        <Publish onClick={props.onPublish} isActive={props.isPublic} />
                    </>
                );
            case EStorageType.stock:
                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} />
                        <Pic
                            kind={props.kind}
                            thumbUrl={props.thumbUrl}
                            isFolder={props.isFolder}
                            isVirus={!!props.isVirus}
                            ext={props.ext || ''}
                        />
                        <ItemName name={props.name} ext={props.ext || ''} />
                        {props.size && <Size size={props.size} />}
                        <Download
                            onClick={props.onDownload}
                            ext={props.ext}
                            size={props?.size}
                            id={props.id}
                            storage={EStorageType.stock}
                        />
                    </>
                );
            case EStorageType.search: {
                const path = props.home === '/' ? 'Облако' : props.home;
                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} />
                        <Pic
                            isFolder={props.isFolder}
                            thumbUrl={props.thumbUrl}
                            kind={props.kind}
                            isVirus={!!props.isVirus}
                            ext={props.ext || ''}
                            id={props.id}
                            isAutoDelete={props?.isAutoDelete}
                        />
                        <ItemName name={props.name} ext={props.ext || ''} />
                        <Destination name={path} />
                        <Favorite isAvailable={!!props.favoritesAvailable} isActive={!!props.isInFavorites} onClick={props.onFavorites} />
                        {props.isFolder ? (
                            <Expires expires={props.publicExpires} isAutoDelete={props.isAutoDelete} />
                        ) : (
                            <Date date={props.mtime} isWide />
                        )}
                        <Size size={props.size} />
                        <Download
                            onClick={props.onDownload}
                            ext={props.isFolder ? 'zip' : props.ext}
                            size={props?.size}
                            id={props.id}
                            storage={EStorageType.search}
                        />
                        <Publish onClick={props.onPublish} isActive={props.isPublic} />
                    </>
                );
            }
            case EStorageType.feed: {
                const path = props.home === '/' ? 'Облако' : props.home.slice(0, props.home.length);
                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} />
                        <Pic isFolder={props.isFolder} thumbUrl={props.thumbUrl} isVirus={!!props.isVirus} ext={props.ext || ''} />
                        <ItemName name={props.name} ext={props.ext || ''} />
                        <Destination name={path} />
                        <Date date={props.mtime} />
                        <Size size={props.size} />
                        <Download onClick={props.onDownload} ext={props.ext} size={props?.size} id={props.id} storage={EStorageType.feed} />
                        <Publish onClick={props.onPublish} isActive={props.isPublic} />
                    </>
                );
            }
            case EStorageType.gallery: {
                const path = props.home === '/' ? 'Облако' : props.home.slice(0, props.home.length);
                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} />
                        <Pic isFolder={false} thumbUrl={props.thumbUrl} isVirus={!!props.isVirus} ext={props.ext || ''} />
                        <ItemName name={props.name} ext={props.ext || ''} />
                        <Favorite isAvailable={!!props.favoritesAvailable} isActive={!!props.isInFavorites} onClick={props.onFavorites} />
                        <Destination name={path} />
                        <Date date={props.mtime} />
                        <Size size={props.size} />
                        <Download onClick={props.onDownload} ext={props.ext} size={props?.size} id={props.id} storage={EStorageType.feed} />
                        <Publish onClick={props.onPublish} isActive={props.isPublic} />
                    </>
                );
            }
            case EStorageType.attaches:
                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} />
                        <Pic
                            isFolder={props.isFolder}
                            thumbUrl={props.thumbUrl}
                            isVirus={false}
                            ext={props.ext || ''}
                            author={props.owner}
                        />
                        <ItemName name={props.name} ext={props.ext || ''} />
                        <Destination name={props.subject} icon={<AttachIcon />} />
                        <Date date={props.mtime} />
                        <Size size={props.size} />
                        <Download
                            onClick={props.onDownload}
                            ext={props.ext}
                            size={props.size}
                            id={props.id}
                            storage={EStorageType.attaches}
                        />
                    </>
                );
            case EStorageType.trashbin: {
                const path = props.deletedFrom === '/' ? props.binName : props.deletedFrom.slice(0, props.deletedFrom.length - 1);

                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} />
                        <Pic isFolder={props.isFolder} thumbUrl={props.thumbUrl} kind={props.kind} isVirus={false} ext={props.ext || ''} />
                        <ItemName name={props.name} ext={props.ext || ''} />
                        <Destination name={path} />
                        <Date date={props.deletedAt} />
                        <Size size={props.size} />
                        <Restore onClick={props.onRestore} />
                    </>
                );
            }
            case EStorageType.quotaCleaner: {
                return (
                    <>
                        <Checkbox onCheckboxClick={props.onCheckboxClick} isSelected={props.isSelected} />
                        <Pic
                            isFolder={props.isFolder}
                            thumbUrl={props.thumbUrl}
                            kind={props.kind}
                            isVirus={props.isVirus}
                            ext={props.ext}
                            author={props.author}
                            isAuthorView={!!props.author}
                        />
                        {getTip()}
                        <ItemName name={props.name} ext={props.ext} />
                        <Date date={props.date} />
                        <Size size={props.size} />
                        <Download onClick={props.onDownload} ext={props.ext} size={props.size} id={props.id} storage={props.storage} />
                    </>
                );
            }
        }
    };

    return (
        <div
            className={classNames({
                [styles.root]: true,
                [styles.root_desktop]: !isMobile,
                [styles.root_selected]: isSelected,
                [styles.root_active]: isActive,
                [styles.root_selecting]: isSelecting,
            })}
        >
            {renderItem(props)}
        </div>
    );
});

DataListItemRow.displayName = 'DataListItemRow';
DataListItemRow.whyDidYouRender = true;
